import { useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { useEnnismoreApiClientConfig } from '@/api';
import { V1GetBookingStatusResponse } from '@/api/clients/ohip';
import { useHotelConfigBySlugOrFail } from '@/brand';

import { BookingError } from '../errors/booking.error';
import { getBookingServiceClient } from '../services/client';
import { useSessionId } from '@/session/use-session-id.hook';

interface BookingStatusArgs {
  bookingId: string;
  hotelReferenceId: string;
}

function getQueryKey(bookingId: string) {
  return ['booking-status', bookingId];
}

/**
 * Responsible for managing the fetching of the booking status query.
 */
export function useBookingStatusQuery(
  { bookingId, hotelReferenceId }: BookingStatusArgs,
  options?: { refetchInterval?: number | false; enabled?: boolean }
) {
  const config = useEnnismoreApiClientConfig();
  const { dialect } = useHotelConfigBySlugOrFail(hotelReferenceId);
  const sessionId = useSessionId();
  const client = getBookingServiceClient(config, dialect);

  return useQuery({
    queryKey: getQueryKey(bookingId),
    queryFn: async () => {
      return client.getBookingStatus({
        bookingId,
        hotelReferenceId,
        sessionId,
      });
    },
    refetchInterval: options?.refetchInterval,
    enabled: options?.enabled,
  });
}

/**
 * Returns the booking status for the given booking from the cache.
 * Note: This does not trigger the query! That is handled at the top level by usePollBooking.
 */
export function useBookingStatus(args: BookingStatusArgs) {
  const { data, error } = useBookingStatusQuery(args, { enabled: false });
  if (data) return data;
  // Surface NOT_FOUND as EXPIRED so callers (polling, redirect, UI) can converge instead of looping.
  if (error instanceof BookingError && error.isBookingNotFound) {
    return {
      bookingId: args.bookingId,
      status: 'EXPIRED',
      previousStatus: 'NONE',
    } satisfies V1GetBookingStatusResponse;
  }
  return undefined;
}

export function useUpdateBookingStatusCache() {
  const client = useQueryClient();

  return useCallback(
    (bookingId: string, newStatus: V1GetBookingStatusResponse['status']) => {
      return client.setQueryData<V1GetBookingStatusResponse>(
        getQueryKey(bookingId),
        (values) => {
          // Add defaults if we don't have existing data
          if (!values) {
            return {
              bookingId,
              status: newStatus,
              previousStatus: 'NONE' as const,
            };
          }

          return {
            bookingId: values.bookingId,
            status: newStatus,
            previousStatus: values.status,
          };
        }
      );
    },
    [client]
  );
}
